'use client'

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import { FeatureCard } from '@/components/tools/FeatureCard'
import { Button } from '@/components/ui/button'
import { useToolUsage } from '@/lib/use-tool-usage'
import { detectBrandFromDomain } from '@/lib/detect-brand-client'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import YouTubeResults, {
  type YouTubeMonitorResult,
} from './components/YouTubeResults'
import { UsageLimitGate, UsageCounter } from '@/components/tools/UsageLimitGate'

export default function YouTubeBrandMonitorPage() {
  const [brand, setBrand] = useState('')
  const [keywords, setKeywords] = useState('')
  const [domain, setDomain] = useState('')
  const [detecting, setDetecting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<YouTubeMonitorResult | null>(null)

  const toolUsage = useToolUsage('youtube-monitor')
  const gate = useToolAccessGate('youtube-monitor', 'YouTube Brand Monitor')

  async function runDetect(value: string) {
    const v = value.trim()
    if (!v) return
    setDetecting(true)
    const d = await detectBrandFromDomain(v)
    // Only fill an EMPTY field. This used to overwrite unconditionally, so a
    // brand the user had typed themselves was silently replaced by whatever we
    // resolved — the one value in the flow that is definitionally authoritative.
    // Matches the guard the citation and reddit forms already had.
    if (d?.brand && !brand.trim()) {
      setBrand(d.brand)
    }
    if (d?.keywords && !keywords.trim()) setKeywords(d.keywords)
    setDetecting(false)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let brandClean = brand.trim()
    // If the user gave a domain but no brand, detect it first so they can
    // run with a URL like every other tool.
    if (!brandClean && domain.trim()) {
      const d = await detectBrandFromDomain(domain)
      if (d?.brand) {
        brandClean = d.brand
        setBrand(d.brand)
        if (d.keywords && !keywords.trim()) setKeywords(d.keywords)
      }
    }
    if (!brandClean) {
      setError('Enter your domain or a brand name.')
      return
    }
    if (!gate.ready) {
      gate.openGate(brandClean)
      return
    }
    setError(null)
    setResult(null)
    setIsLoading(true)
    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/youtube-monitor',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand: brandClean, keywords }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(brandClean)
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(
          data.message || 'YouTube monitoring failed. Please try again.'
        )
      }

      setResult(data as YouTubeMonitorResult)
      gate.markConsumed()

      setTimeout(() => {
        document
          .getElementById('youtube-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='animate-fade-in'>
      {gate.modal}
      <div className='container mx-auto px-4 py-16 pb-24'>
        {/* Hero */}
        <LenisFadeIn>
          <div className='text-center mb-10'>
            <h1 className='mb-4 font-heading max-w-4xl mx-auto'>
              How Does Your Brand Show Up On YouTube?
            </h1>
            <p className='lead max-w-2xl mx-auto mb-6'>
              Track brand mentions across YouTube videos. AI search engines
              weight YouTube heavily for brand-related queries because video
              transcripts and metadata are major training signals.
            </p>

            <p className='text-sm text-muted-foreground max-w-2xl mx-auto mb-8'>
              YouTube content shapes how AI systems describe your brand. This
              tool surfaces every video mentioning your brand, the channels
              behind them, total reach, sentiment, and recency. Use it to spot
              coverage gaps, monitor sentiment shifts, and find creators worth
              partnering with for sponsored reviews.
            </p>

            <form
              onSubmit={handleSubmit}
              className='max-w-xl mx-auto space-y-3'
            >
              <input
                type='text'
                value={domain}
                onChange={e => setDomain(e.target.value)}
                onBlur={() => runDetect(domain)}
                placeholder='Enter your domain (e.g. notion.so)'
                maxLength={200}
                className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors'
                disabled={isLoading}
              />
              <input
                type='text'
                value={brand}
                onChange={e => setBrand(e.target.value)}
                placeholder={
                  detecting
                    ? 'Detecting brand...'
                    : 'Brand (auto-detected, edit if needed)'
                }
                maxLength={100}
                className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors'
                disabled={isLoading}
              />
              <input
                type='text'
                value={keywords}
                onChange={e => setKeywords(e.target.value)}
                placeholder='Optional: keywords (review, tutorial, vs)'
                maxLength={200}
                className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors'
                disabled={isLoading}
              />
              <Button
                type='submit'
                variant='default'
                size='lg'
                loading={isLoading}
                disabled={isLoading}
                className='w-full sm:w-auto'
              >
                {isLoading ? 'Scanning YouTube...' : 'Run YouTube audit'}
              </Button>
            </form>

            {isLoading && (
              <p className='text-sm text-muted-foreground mt-4'>
                Searching YouTube, pulling transcripts, and analyzing sentiment.
                This takes 20-30 seconds.
              </p>
            )}

            {error && (
              <p className='text-sm text-[#F90B8A] mt-4 bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {error}
              </p>
            )}
          </div>
        </LenisFadeIn>

        {/* Results */}
        <div id='youtube-results' className='max-w-4xl mx-auto'>
          {isLoading && (
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-12 flex flex-col items-center gap-4'>
              <Loader2 className='w-8 h-8 animate-spin text-[#008dcc]' />
              <p className='text-sm text-muted-foreground'>
                Searching YouTube, pulling transcripts, and scoring...
              </p>
            </div>
          )}
          {toolUsage.isGated && !isLoading && (
            <UsageLimitGate
              toolName='YouTube Brand Monitor'
              used={toolUsage.used}
              limit={toolUsage.limit}
            />
          )}
          {result && !isLoading && !toolUsage.isGated && (
            <>
              <YouTubeResults result={result} />
              <UsageCounter
                used={toolUsage.used}
                limit={toolUsage.limit}
                isTokenHolder={toolUsage.isTokenHolder}
              />
            </>
          )}
        </div>

        {/* Pre-results info */}
        {!result && !isLoading && (
          <LenisFadeIn>
            <div className='max-w-3xl mx-auto mt-12'>
              <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
                <h2 className='text-lg font-semibold text-foreground mb-4 text-center'>
                  What we measure
                </h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {[
                    {
                      title: 'Mention Volume',
                      desc: 'Counts every YouTube video that mentions your brand in title, description, or channel name.',
                      color: '#008dcc',
                    },
                    {
                      title: 'Channel Diversity',
                      desc: 'Number of unique channels covering your brand. Diverse coverage signals organic interest to AI search engines.',
                      color: '#8b5cf6',
                    },
                    {
                      title: 'Total Reach',
                      desc: 'Aggregate view count across all matched videos. Tells you the actual audience your brand reaches on YouTube.',
                      color: '#FDC304',
                    },
                    {
                      title: 'Transcript-Grounded Sentiment',
                      desc: 'We fetch transcripts for your top 5 videos by views and run sentiment on the actual spoken content, not just titles. Catches sarcasm and late-video reversals.',
                      color: '#22c55e',
                    },
                    {
                      title: 'Recency',
                      desc: 'How fresh your YouTube footprint is. AI models weight recent content heavily for active brands.',
                      color: '#f97316',
                    },
                    {
                      title: 'AI-Generated Action Plan',
                      desc: 'GPT-4o-mini reviews your full result and generates a custom 3-step plan to lift YouTube visibility.',
                      color: '#F90B8A',
                    },
                  ].map(item => (
                    <FeatureCard key={item.title} {...item} />
                  ))}
                </div>
              </div>
            </div>
          </LenisFadeIn>
        )}

        {/* Methodology */}
        <LenisFadeIn>
          <div className='max-w-4xl mx-auto mt-16'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-2 text-center'>
                Why YouTube Coverage Shapes AI Visibility
              </h2>
              <p className='text-sm text-muted-foreground text-center mb-8 max-w-2xl mx-auto'>
                Video transcripts, titles, descriptions, and channel metadata
                are part of the training corpus for every major AI assistant.
                Strong YouTube coverage is one of the highest-leverage off-site
                signals for AI brand recall.
              </p>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='bg-background border border-line-strong rounded-xl p-5'>
                  <p className='text-xs font-semibold text-[#008dcc] uppercase tracking-wider mb-2'>
                    Live YouTube data
                  </p>
                  <p className='text-sm text-foreground/80'>
                    We query the YouTube Data API live on every audit. Results
                    reflect current video availability, view counts, and
                    publication dates, not a stale prompt library.
                  </p>
                </div>
                <div className='bg-background border border-line-strong rounded-xl p-5'>
                  <p className='text-xs font-semibold text-[#008dcc] uppercase tracking-wider mb-2'>
                    Five-dimension score
                  </p>
                  <p className='text-sm text-foreground/80'>
                    Volume (25), Channel Diversity (20), Sentiment (20), Reach
                    (20), and Recency (15) sum to a 100-point AI visibility
                    grade for your YouTube footprint.
                  </p>
                </div>
                <div className='bg-background border border-line-strong rounded-xl p-5'>
                  <p className='text-xs font-semibold text-[#008dcc] uppercase tracking-wider mb-2'>
                    Layer 2 sentiment, not just titles
                  </p>
                  <p className='text-sm text-foreground/80'>
                    For your top 5 videos by views, we pull the full transcript
                    and analyze the actual spoken content. Each video shows
                    whether sentiment came from the transcript or the metadata.
                    Channels with concentrated negative coverage are flagged in
                    the recommendations.
                  </p>
                </div>
                <div className='bg-background border border-line-strong rounded-xl p-5'>
                  <p className='text-xs font-semibold text-[#008dcc] uppercase tracking-wider mb-2'>
                    Action plan, not just a number
                  </p>
                  <p className='text-sm text-foreground/80'>
                    The AI summary turns the score into specific next moves,
                    such as which channels to pitch and which negative videos to
                    address.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </LenisFadeIn>
      </div>
    </div>
  )
}
